import React, { useState } from 'react';
import { CARS } from '../../constants/columns';
import { useTable, useGlobalFilter, usePagination, useSortBy } from 'react-table';
import { stockCarsOptions } from '../../constants/filterOptions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setFilters } from '../../utils/setFilters';
import describeFilters from '../../utils/describeFilters';
import { parseTableQuery, buildTableQuery } from '../../utils/tableQuery';
import { sortCarsParams } from '../../constants/sortParams';

// components
import Header from '../../components/Header/Header';
import Table from '../../components/Table/Table';
import TableTabs from '../../components/TableTabs/TableTabs';
import Empty from '../../components/Empty/Empty';
import HookForm from '../../components/HookForm/HookForm';

// icons
import icon from '../../brand/generated/sidebar/cars.svg';
import getItems from '../../api/cars/getItems';
import getStockFilters from '../../api/cars/getStockFilters';
import bucketIcon from '../../brand/generated/popover/delete.svg';
import { PERMISSION_CAN_DELETE_DEALS, PERMISSION_CAN_EDIT_AUTO_AND_EQUIPMENTS } from '../../constants/roles';
import isUserHavePermission from '../../utils/isUserHavePermission';
import alert from '../../helpers/alert';
import removeCar from '../../api/cars/removeCar';
import extractErrorMessage from '../../utils/extractErrorMessage';

const tableStyle = { backgroundColor: 'white' };

// Эта страница всегда должна показывать только сток (forApp=true + активная
// запись в dealers_stock) — то же самое, что делает мобильное приложение при
// показе каталога. Именно поэтому STOCK_SCOPE не идёт через defaultQueryParams
// как в pages/Car/Cars.jsx: там применение/сброс фильтров через TableTabs
// полностью заменяет queryParams (см. utils/setFilters.js), что стёрло бы эту
// область видимости. Вместо этого STOCK_SCOPE всегда домешивается в параметры
// запроса последним — уже после пользовательских фильтров — и не может быть
// случайно снят через "Расширенный поиск"/"Сбросить все"
const STOCK_SCOPE = { forApp: true, stock: true };

let sortCount = 1;

const FILTER_NAMES = [...stockCarsOptions.map((option) => option.name), 'all'];

const StockCars = () => {
  const [columns, setColumns] = React.useState(CARS);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Разбираем адрес один раз: дальше адрес пишем мы сами, и перечитывать его
  // на каждый рендер значило бы гоняться за собственной записью.
  const fromUrl = React.useRef(parseTableQuery(searchParams, FILTER_NAMES)).current;
  const [loading, setLoading] = useState(true);
  const [cars, setCars] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [pagination, setPagination] = useState({
    page: fromUrl.page,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
    pageSize: fromUrl.pageSize || 10,
  });
  const [isFetchAll, setFetchAll] = useState(false);

  const table = useTable(
    {
      columns,
      data: cars,
      initialState: {
        pageSize: pagination.pageSize,
      },
      manualPagination: true,
      pageCount: pagination.totalPages,
      page: pagination.page - 1,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const { setGlobalFilter, page, setPageSize } = table;

  const [value, setValue] = useState(fromUrl.filters.all || '');
  const [queryParams, setQueryParams] = useState(fromUrl.filters);
  const [appliedFilters, setAppliedFilters] = useState([]);
  // Варианты фильтров со счётчиками; кладём их прямо в опции формы — HookForm
  // спредит объект опции на компонент, так каждый селект получает свой список.
  const [facets, setFacets] = useState({});
  const filtersFormRef = React.useRef(null);
  const filtersTimer = React.useRef(null);
  const lastCityRef = React.useRef(fromUrl.filters.cityId ? Number(fromUrl.filters.cityId) : null);
  // Пока раскладываем фильтры из адреса обратно в форму, её же события игнорируем:
  // иначе они сбросили бы восстановленную страницу на первую.
  const restoringRef = React.useRef(false);
  const restoredRef = React.useRef(false);

  stockCarsOptions.forEach((option) => {
    option.options = facets[option.name] || [];
  });

  const handleNavigate = (index) => {
    if (!isUserHavePermission(PERMISSION_CAN_EDIT_AUTO_AND_EQUIPMENTS)) {
      return;
    }
    navigate(`/cars/car/${index}/main-data`, { state: cars.find((item) => item.id === index) });
  };

  const init = async (page) => {
    setLoading(true);
    await getItems({ ...page, ...STOCK_SCOPE })
      .then((res) => {
        setCars(res.data.cars);
        setPagination({
          ...res.data.pagination,
          page: res.data.pagination?.pageNumber,
          pageSize: pagination.pageSize,
        });
        setDataCount(res.data.pagination?.count);
      })
      .catch((error) => {})
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    if (!isFetchAll) {
      init({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
    }
  }, [pagination.page, pagination.pageSize, queryParams]);

  // Фильтры и страница живут в адресе: ушёл в карточку машины, вернулся назад —
  // список открылся ровно там же. replace, а не push: иначе «назад» пришлось бы
  // жать столько раз, сколько полей менеджер потрогал.
  React.useEffect(() => {
    setSearchParams(buildTableQuery(queryParams, pagination.page, pagination.pageSize), {
      replace: true,
    });
  }, [queryParams, pagination.page, pagination.pageSize]);

  // Списки значений пересчитываются под текущий фильтр: выбрали город — в «ДЦ»
  // остаются только его центры, и у каждого варианта видно, сколько машин будет.
  React.useEffect(() => {
    getStockFilters({ ...queryParams, ...STOCK_SCOPE })
      .then((res) => setFacets(res?.data || {}))
      .catch(() => {});
  }, [queryParams]);

  const applyFilters = (resetFilters) => {
    clearTimeout(filtersTimer.current);
    if (resetFilters) {
      lastCityRef.current = null;
      // Иначе восстановленные из адреса значения вернулись бы при следующем
      // открытии панели: форма монтируется из defaultValue.
      stockCarsOptions.forEach((option) => delete option.defaultValue);
    }
    setFilters(resetFilters, setFetchAll, setPageSize, setPagination, setQueryParams, stockCarsOptions);
    setAppliedFilters(resetFilters ? [] : describeFilters(stockCarsOptions));
  };

  // Фильтр применяется сам, без кнопки; задержка гасит лишние запросы, пока
  // менеджер щёлкает несколько полей подряд.
  const handleFiltersChange = React.useCallback((values) => {
    if (restoringRef.current) return;
    // Сменили город — ДЦ из прежнего города дал бы пустую таблицу, снимаем его.
    const cityId = values?.cityId?.value ?? null;
    if (cityId !== lastCityRef.current) {
      lastCityRef.current = cityId;
      if (values?.dcId) {
        const dc = stockCarsOptions.find((item) => item.name === 'dcId');
        if (dc) {
          delete dc.value;
          delete dc.defaultValue;
        }
        filtersFormRef.current?.setValue('dcId', null);
      }
    }

    clearTimeout(filtersTimer.current);
    filtersTimer.current = setTimeout(() => applyFilters(false), 400);
  }, []);

  React.useEffect(() => () => clearTimeout(filtersTimer.current), []);

  // Подписи для значений из адреса берём из фасетов: в URL лежит только id.
  React.useEffect(() => {
    if (restoredRef.current || !Object.keys(facets).length) return;
    restoredRef.current = true;

    restoringRef.current = true;
    stockCarsOptions.forEach((option) => {
      const raw = fromUrl.filters[option.name];
      if (raw === undefined) return;
      const known = (facets[option.name] || []).find((item) => String(item.value) === String(raw));
      const restored = known
        ? { value: known.value, label: known.label }
        : { value: raw, label: String(raw) };
      option.value = restored;
      // defaultValue, а не только setValue: панель могли ещё не раскрыть, и тогда
      // форма смонтируется позже — на пустых значениях, затерев восстановленные.
      option.defaultValue = restored;
      filtersFormRef.current?.setValue(option.name, restored);
    });
    setAppliedFilters(describeFilters(stockCarsOptions));
    restoringRef.current = false;
  }, [facets]);

  const removeFilter = (name) => {
    const option = stockCarsOptions.find((item) => item.name === name);
    if (option) {
      delete option.value;
      delete option.defaultValue;
    }
    filtersFormRef.current?.setValue(name, null);
    applyFilters(false);
  };

  const handleActionPopover = async (carIndex, actionIndex, title, id) => {};

  const sortBy = (value) => {
    queryParams.sortBy = sortCarsParams.get(value);
    if (!sortCarsParams.get(value) || (sortCount > 1 && sortCarsParams.get(value) !== queryParams.sortBy)) {
      sortCount = 1;
      return;
    }
    if (sortCount === 3) {
      sortCount = 1;
    }
    if (sortCarsParams.get(value) === queryParams.sortBy && sortCount === 1) {
      queryParams.sortBy = sortCarsParams.get(value);
      queryParams.sort = 'ask';
      sortCount++;
    } else if (sortCarsParams.get(value) === queryParams.sortBy && sortCount === 2) {
      queryParams.sortBy = sortCarsParams.get(value);
      queryParams.sort = 'desc';
      sortCount++;
    }
    setFetchAll(false);
    setQueryParams({ ...queryParams });
  };

  const popover = [
    {
      title: 'Удалить автомобиль',
      icon: bucketIcon,
      onClick: async (row) => {
        removeCar({ id: row?.id })
          .then(() => {
            init({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
          })
          .catch((err) => {
            alert.error(extractErrorMessage(err?.response?.data) || 'Ошибка при удалении автомобиля');
          });
      },
      permission: PERMISSION_CAN_DELETE_DEALS,
    },
  ];

  const onFastSearch = (value) => {
    setPagination({
      ...pagination,
      page: 1,
    });

    setQueryParams({
      ...queryParams,
      all: value,
    });
  };

  return (
    <>
      <Header title="Сток автомобилей" icon={icon} />
      <br />
      <br />
      <br />
      <br />
      <TableTabs
        setFilter={onFastSearch}
        value={value}
        setValue={setValue}
        filterComponent={
          <HookForm
            ref={filtersFormRef}
            inputs={stockCarsOptions}
            onValuesChange={handleFiltersChange}
            grid={{
              display: 'grid',
              gap: '10px',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
            }}
            responsiveForm
            style={{ backgroundColor: 'transparent' }}
          />
        }
        defaultFiltersOpen={Object.keys(fromUrl.filters).some((name) => name !== 'all')}
        handleSearchParams={applyFilters}
        appliedFilters={appliedFilters}
        onRemoveFilter={removeFilter}
        inputPlaceholder={'Быстрый поиск по стоковым автомобилям'}
        // Кнопки "Добавить" тут нет — стоковые авто попадают в каталог только
        // через загрузку файла на странице "Загрузка стока", не вручную отсюда.
        // permission на заведомо несуществующее значение — скрывает пустую
        // IconButton, которую TableTabs иначе рендерит всегда (permission по
        // умолчанию 'all')
        permission="__none__"
      />
      <br />
      {!page.length && !loading ? (
        <Empty />
      ) : (
        <Table
          table={table}
          all={'автомобилей'}
          style={tableStyle}
          handleNavigate={handleNavigate}
          pagination
          sort
          setPagination={setPagination}
          setFetchAll={setFetchAll}
          isFetchAll={isFetchAll}
          setData={setCars}
          p={pagination}
          loading={loading}
          setLoading={setLoading}
          columns={columns}
          setColumns={setColumns}
          columnsSettingsKey="stockCars"
          queryParams={{ ...queryParams, ...STOCK_SCOPE }}
          api={'/api/shop/getItems'}
          response={'cars'}
          popover={popover}
          handleActionPopover={handleActionPopover}
          dataCount={dataCount}
          handleSortTable={sortBy}
        />
      )}
    </>
  );
};

export default StockCars;
